import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Avatar,
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Stack,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Skeleton,
  Pagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import PushPinIcon from '@mui/icons-material/PushPin';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TagIcon from '@mui/icons-material/Tag';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { CommunityApi } from '../../services/api';
import QoreConnectPageHero from './QoreConnectPageHero';
import { PAGE_META } from './qoreConnectConfig';
import './QoreConnectPages.css';

// ─── Category config ──────────────────────────────────────────────────────────
const CATEGORIES = [
  { id: 'all',       label: 'All',          color: '#6366f1' },
  { id: 'general',   label: 'General',      color: '#8b5cf6' },
  { id: 'career',    label: 'Career',       color: '#10b981' },
  { id: 'ai-ml',     label: 'AI/ML',        color: '#ec4899' },
  { id: 'technical', label: 'Technical',    color: '#f59e0b' },
  { id: 'learning',  label: 'Courses',      color: '#06b6d4' },
  { id: 'green-tech',label: 'Green Tech',   color: '#84cc16' },
  { id: 'showcase',  label: 'Showcase',     color: '#f97316' },
];

const catMap = Object.fromEntries(CATEGORIES.map((c) => [c.id, c]));

// ─── Time ago helper ──────────────────────────────────────────────────────────
function timeAgo(date) {
  if (!date) return '';
  const diff = (Date.now() - new Date(date)) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── Avatar colour helper ─────────────────────────────────────────────────────
const AV_COLORS = ['#6366f1','#8b5cf6','#ec4899','#f59e0b','#10b981','#06b6d4','#ef4444'];
function initials(first = '', last = '') {
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase() || '?';
}
function avColor(name = '') {
  return AV_COLORS[name.charCodeAt(0) % AV_COLORS.length];
}

// ─── Trending threshold ───────────────────────────────────────────────────────
const isTrending = (d) => (d.votes || 0) + (d.replies?.length || 0) * 2 + (d.views || 0) * 0.1 > 25;

// ─── Post card ────────────────────────────────────────────────────────────────
function PostCard({ discussion, onClick, onLike }) {
  const theme = useTheme();
  const cat = catMap[discussion.category] || CATEGORIES[1];
  const author = discussion.author || {};
  const authorName = `${author.firstName || ''} ${author.lastName || ''}`.trim() || 'Anonymous';
  const liked = discussion._liked;

  return (
    <Card
      elevation={0}
      sx={{
        bgcolor: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: '12px',
        transition: 'box-shadow 0.2s, border-color 0.2s',
        '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.4)', borderColor: 'rgba(255,255,255,0.14)' },
        position: 'relative',
      }}
    >
      {/* Badges */}
      {discussion.isPinned && (
        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
          <Chip icon={<PushPinIcon />} label="Pinned" size="small" color="warning" />
        </Box>
      )}
      {isTrending(discussion) && !discussion.isPinned && (
        <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1 }}>
          <Chip icon={<LocalFireDepartmentIcon />} label="Trending" size="small" color="error" />
        </Box>
      )}

      <CardActionArea onClick={() => onClick(discussion)} sx={{ p: 0 }}>
        <CardContent sx={{ pb: '8px !important' }}>
          {/* Author row */}
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
            <Avatar
              src={author.profileImage}
              sx={{ width: 36, height: 36, bgcolor: avColor(authorName), fontSize: 13, fontWeight: 700 }}
            >
              {!author.profileImage && initials(author.firstName, author.lastName)}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle2" noWrap>{authorName}</Typography>
                {author.role && (
                  <Chip label={author.role} size="small" sx={{ height: 18, fontSize: 10 }} />
                )}
              </Stack>
              <Typography variant="caption" color="text.secondary">
                {timeAgo(discussion.createdAt)}
              </Typography>
            </Box>
            <Chip
              label={cat.label}
              size="small"
              sx={{ bgcolor: `${cat.color}20`, color: cat.color, borderColor: `${cat.color}40`, border: '1px solid' }}
            />
          </Stack>

          {/* Title */}
          <Typography
            variant="subtitle1"
            fontWeight={700}
            gutterBottom
            sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#fafafa' }}
          >
            {discussion.title}
            {discussion.isSolved && <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />}
          </Typography>

          {/* Excerpt */}
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', mb: 1.5 }}
          >
            {discussion.content}
          </Typography>

          {/* Tags */}
          {discussion.tags?.length > 0 && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mb: 1 }}>
              {discussion.tags.slice(0, 4).map((tag) => (
                <Chip key={tag} icon={<TagIcon sx={{ fontSize: '12px !important' }} />} label={tag} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
              ))}
            </Stack>
          )}
        </CardContent>
      </CardActionArea>

      {/* Footer stats */}
      <Divider />
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ px: 2, py: 1, color: 'text.secondary' }}
      >
        <Stack direction="row" spacing={0.5} alignItems="center">
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onLike(discussion); }}
            color={liked ? 'error' : 'default'}
          >
            {liked ? <FavoriteIcon fontSize="small" /> : <FavoriteBorderIcon fontSize="small" />}
          </IconButton>
          <Typography variant="caption">{discussion.votes || 0}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">{discussion.replies?.length || 0}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <VisibilityOutlinedIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">{discussion.views || 0}</Typography>
        </Stack>
      </Stack>
    </Card>
  );
}

// ─── Skeleton card ────────────────────────────────────────────────────────────
function PostSkeleton() {
  return (
    <Card elevation={0} sx={{ bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '12px' }}>
      <CardContent>
        <Stack direction="row" spacing={1.5} mb={1.5}>
          <Skeleton variant="circular" width={36} height={36} />
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="20%" />
          </Box>
        </Stack>
        <Skeleton variant="text" width="70%" sx={{ mb: 0.5 }} />
        <Skeleton variant="text" />
        <Skeleton variant="text" width="85%" />
      </CardContent>
    </Card>
  );
}

// ─── New Discussion Dialog ────────────────────────────────────────────────────
function NewDiscussionDialog({ open, onClose, onSubmit }) {
  const [form, setForm] = useState({ title: '', content: '', category: 'general', tags: [] });
  const [tagInput, setTagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const tag = tagInput.trim().toLowerCase();
      if (!form.tags.includes(tag)) set('tags', [...form.tags, tag]);
      setTagInput('');
    }
  };

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    setSubmitting(true);
    await onSubmit(form);
    setSubmitting(false);
    setForm({ title: '', content: '', category: 'general', tags: [] });
    setTagInput('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight={700}>Start a Discussion</Typography>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>
        <TextField
          label="Title"
          placeholder="What's on your mind?"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          fullWidth
          required
        />

        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select value={form.category} label="Category" onChange={(e) => set('category', e.target.value)}>
            {CATEGORIES.filter((c) => c.id !== 'all').map((c) => (
              <MenuItem key={c.id} value={c.id}>{c.label}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          label="Content"
          placeholder="Share your thoughts, questions, or ideas…"
          value={form.content}
          onChange={(e) => set('content', e.target.value)}
          multiline
          minRows={5}
          fullWidth
          required
        />

        <TextField
          label="Tags (press Enter to add)"
          placeholder="python, machine-learning, career…"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={addTag}
          fullWidth
        />
        {form.tags.length > 0 && (
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {form.tags.map((t) => (
              <Chip
                key={t}
                label={`#${t}`}
                size="small"
                onDelete={() => set('tags', form.tags.filter((x) => x !== t))}
              />
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || !form.title.trim() || !form.content.trim()}
          startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
        >
          {submitting ? 'Posting…' : 'Post Discussion'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;

const Forum = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user, token } = useAuth();
  const meta = PAGE_META.forum;

  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('recent');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchDiscussions = useCallback(async () => {
    setLoading(true);
    try {
      const params = { sort: sortBy, limit: 50 };
      if (activeCategory !== 'all') params.category = activeCategory;
      if (searchQuery) params.search = searchQuery;

      const res = await CommunityApi.getDiscussions(params);
      const data = res?.data || res?.discussions || res || [];
      setDiscussions(Array.isArray(data) ? data : []);
      setTotalPages(Math.max(1, Math.ceil((Array.isArray(data) ? data.length : 0) / PAGE_SIZE)));
    } catch {
      setDiscussions([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory, sortBy, searchQuery]);

  useEffect(() => { fetchDiscussions(); }, [fetchDiscussions]);
  useEffect(() => { setPage(1); }, [activeCategory, searchQuery, sortBy]);

  const handleLike = async (discussion) => {
    if (!token) return;
    try {
      await CommunityApi.likeDiscussion(token, discussion._id || discussion.id);
      setDiscussions((prev) =>
        prev.map((d) =>
          (d._id || d.id) === (discussion._id || discussion.id)
            ? { ...d, votes: (d.votes || 0) + (d._liked ? -1 : 1), _liked: !d._liked }
            : d
        )
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreatePost = async (form) => {
    try {
      await CommunityApi.createDiscussion(token, form);
      setDialogOpen(false);
      fetchDiscussions();
    } catch (err) {
      console.error('Error creating post:', err);
    }
  };

  const paged = discussions.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const sortOptions = [
    { value: 'recent', labelKey: 'qoreConnect.pages.forum.sortRecent' },
    { value: 'popular', labelKey: 'qoreConnect.pages.forum.sortPopular' },
    { value: 'unanswered', labelKey: 'qoreConnect.pages.forum.sortUnanswered' },
  ];

  return (
    <div className="qc-page qc-page--forum">
      <QoreConnectPageHero accent={meta.accent} {...meta}>
        {user ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ borderRadius: '10px', textTransform: 'none', bgcolor: '#06b6d4', '&:hover': { bgcolor: '#0891b2' } }}
          >
            {t('qoreConnect.pages.forum.startDiscussion')}
          </Button>
        ) : (
          <Link to="/login" className="qc-btn qc-btn--violet">
            {t('qoreConnect.pages.forum.signInToPost')}
          </Link>
        )}
      </QoreConnectPageHero>

      <nav className="qc-page-toolbar" aria-label={t('qoreConnect.pages.forum.categories')}>
        <div className="qc-page-toolbar__inner">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.id}
              type="button"
              className={`qc-filter-pill ${activeCategory === cat.id ? 'qc-filter-pill--active' : ''}`}
              onClick={() => { setActiveCategory(cat.id); setPage(1); }}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="qc-page-body">
        <div className="qc-page-grid qc-page-grid--sidebar">
          <aside className="qc-sidebar-card">
            <p className="qc-sidebar-label">{t('qoreConnect.pages.forum.search')}</p>
            <input
              type="search"
              className="qc-search-input"
              placeholder={t('qoreConnect.pages.forum.searchPlaceholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <p className="qc-sidebar-label" style={{ marginTop: '1rem' }}>
              {t('qoreConnect.pages.forum.sortBy')}
            </p>
            {sortOptions.map((s) => (
              <button
                key={s.value}
                type="button"
                className={`qc-sort-btn ${sortBy === s.value ? 'qc-sort-btn--active' : ''}`}
                onClick={() => setSortBy(s.value)}
              >
                {t(s.labelKey)}
              </button>
            ))}
          </aside>

          <div>
            <p className="qc-results-count">
              {loading
                ? t('qoreConnect.pages.forum.loading')
                : t('qoreConnect.pages.forum.results', { count: discussions.length })}
            </p>
            <Stack spacing={2}>
              {loading
                ? Array.from({ length: 5 }).map((_, i) => <PostSkeleton key={i} />)
                : paged.length === 0
                ? (
                    <div className="qc-empty-state">
                      <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: '#64748b' }} />
                      <h3>{t('qoreConnect.pages.forum.emptyTitle')}</h3>
                      <p>{t('qoreConnect.pages.forum.emptyDesc')}</p>
                      {user && (
                        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
                          {t('qoreConnect.pages.forum.emptyCta')}
                        </Button>
                      )}
                    </div>
                  )
                : paged.map((d) => (
                    <PostCard
                      key={d._id || d.id}
                      discussion={d}
                      onClick={() => {}}
                      onLike={handleLike}
                    />
                  ))}
            </Stack>
            {totalPages > 1 && (
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, v) => setPage(v)}
                  color="primary"
                  shape="rounded"
                />
              </Box>
            )}
          </div>
        </div>
      </div>

      {/* ── New Discussion Dialog ────────────────────────────────────────── */}
      <NewDiscussionDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleCreatePost}
      />

    </div>
  );
};

export default Forum;
